import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import api from '../utils/axios';
import { Card } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Button } from '../components/ui/button';
import { Checkbox } from '../components/ui/checkbox';
import { Textarea } from '../components/ui/textarea';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../components/ui/dialog';
import { toast } from 'sonner';
import {
  ArrowLeft,
  Loader2,
  Eye,
  CheckCircle2,
  Ban,
  AlertCircle,
  MailX,
  Clock,
  ShieldCheck,
  RefreshCw,
  XCircle,
} from 'lucide-react';

// Estados del sistema (value/label) — se combinan con los personalizados activos.
const SYS_ESTADOS = [
  { value: 'ABIERTO', label: 'Abierto' },
  { value: 'PENDIENTE', label: 'Pendiente' },
  { value: 'EN_INVESTIGACION', label: 'En Investigación' },
  { value: 'EN_REVISION', label: 'En Revisión' },
  { value: 'ESCALADO', label: 'Escalado' },
  { value: 'ESPERANDO_COMPLIANCE', label: 'Esperando Compliance' },
  { value: 'BLOQUEO_PREVENTIVO', label: 'Bloqueo Preventivo' },
  { value: 'EN_VIGILANCIA', label: 'En Vigilancia' },
  { value: 'CANCELADO', label: 'Cancelado' },
  { value: 'ARCHIVADO', label: 'Archivado' },
];

const RESULT_META = {
  PENDIENTE: { label: 'Pendiente', cls: 'bg-slate-100 text-slate-600 border-slate-200', Icon: Clock },
  PROCESANDO: { label: 'Chequeando…', cls: 'bg-blue-100 text-blue-700 border-blue-200', Icon: Loader2, spin: true },
  LISTA: { label: 'Lista para vigilar', cls: 'bg-green-100 text-green-700 border-green-200', Icon: CheckCircle2 },
  BLOQUEADA: { label: 'Bloqueada en Waya', cls: 'bg-red-100 text-red-700 border-red-200', Icon: Ban },
  YA_CONFIGURADA: { label: 'Ya configurada', cls: 'bg-amber-100 text-amber-700 border-amber-200', Icon: ShieldCheck },
  SIN_MAIL: { label: 'Sin email', cls: 'bg-gray-100 text-gray-600 border-gray-300', Icon: MailX },
};

const TERMINAL = ['COMPLETADO', 'CANCELADO', 'ERROR'];

export default function PrepararVigilancia() {
  const { jobId } = useParams();
  const navigate = useNavigate();
  const [job, setJob] = useState(null);
  const [loading, setLoading] = useState(true);
  const [notFound, setNotFound] = useState(false);
  const [selected, setSelected] = useState({}); // case_id -> bool
  const [ejecutando, setEjecutando] = useState(false);
  const pollRef = useRef(null);
  const autoSelectedRef = useRef(false);
  // Cambio de estado / cierre por caso
  const [allStates, setAllStates] = useState(SYS_ESTADOS);
  const [tiposResolucion, setTiposResolucion] = useState([]);
  const [rowChanged, setRowChanged] = useState({}); // case_id -> nuevo estado label
  const [rowBusy, setRowBusy] = useState({}); // case_id -> bool
  const [closeDlg, setCloseDlg] = useState(null); // {caseId, titulo}
  const [closeTipo, setCloseTipo] = useState('');
  const [closeObs, setCloseObs] = useState('');
  const [closing, setClosing] = useState(false);

  useEffect(() => {
    (async () => {
      try {
        const [stRes, trRes] = await Promise.all([
          api.get('/case-statuses', { skipAuthRedirect: true }),
          api.get('/cases/tipos-resolucion', { skipAuthRedirect: true }),
        ]);
        const custom = (stRes.data?.statuses || []).map((s) => ({ value: s.key, label: s.label }));
        setAllStates([...SYS_ESTADOS, ...custom]);
        const tipos = Array.isArray(trRes.data) ? trRes.data : [];
        setTiposResolucion(tipos);
        if (tipos.length) setCloseTipo(tipos[0]);
      } catch { /* usar defaults */ }
    })();
  }, []);

  const cambiarEstado = async (caseId, estadoValue) => {
    if (!estadoValue) return;
    if (estadoValue === '__CERRAR__') {
      const it = (job?.items || []).find((x) => x.case_id === caseId);
      setCloseObs(it?.estado_cuenta && it.estado_cuenta.toUpperCase().includes('BLOQUE')
        ? 'Cuenta bloqueada en Waya' : '');
      setCloseDlg({ caseId, titulo: it?.titulo || caseId });
      return;
    }
    setRowBusy((p) => ({ ...p, [caseId]: true }));
    try {
      const res = await api.patch(`/cases/${caseId}`, { estado: estadoValue }, { skipAuthRedirect: true });
      if (res.data?._approval_required) {
        setRowChanged((p) => ({ ...p, [caseId]: 'Pendiente Aprobación' }));
        setSelected((p) => { const n = { ...p }; delete n[caseId]; return n; });
        toast.info(res.data._approval_message || 'El cambio requiere aprobación; quedó pendiente.');
      } else {
        const lbl = allStates.find((s) => s.value === estadoValue)?.label || estadoValue;
        setRowChanged((p) => ({ ...p, [caseId]: lbl }));
        setSelected((p) => { const n = { ...p }; delete n[caseId]; return n; });
        toast.success(`Caso → ${lbl}`);
      }
    } catch (e) {
      toast.error(e.response?.data?.detail || 'No se pudo cambiar el estado');
    } finally {
      setRowBusy((p) => ({ ...p, [caseId]: false }));
    }
  };

  const doClose = async () => {
    if (!closeDlg) return;
    if (!closeObs.trim()) { toast.error('Escribí una observación de cierre'); return; }
    setClosing(true);
    try {
      await api.post('/cases/bulk-close', {
        case_ids: [closeDlg.caseId],
        tipo_resolucion: closeTipo || 'Otro',
        observaciones: closeObs.trim(),
      }, { skipAuthRedirect: true });
      setRowChanged((p) => ({ ...p, [closeDlg.caseId]: 'Cerrado' }));
      setSelected((p) => { const n = { ...p }; delete n[closeDlg.caseId]; return n; });
      toast.success('Caso cerrado');
      setCloseDlg(null);
      setCloseObs('');
    } catch (e) {
      toast.error(e.response?.data?.detail || 'No se pudo cerrar el caso');
    } finally {
      setClosing(false);
    }
  };

  const stopPoll = () => {
    if (pollRef.current) { clearInterval(pollRef.current); pollRef.current = null; }
  };

  const tick = useCallback(async () => {
    try {
      const r = await api.get(`/cases/vigilar/preparar/${jobId}`, { skipAuthRedirect: true });
      setJob(r.data);
      setLoading(false);
      if (TERMINAL.includes(r.data?.estado)) {
        stopPoll();
        // Auto-seleccionar las que quedaron LISTA (solo una vez)
        if (!autoSelectedRef.current) {
          autoSelectedRef.current = true;
          const auto = {};
          (r.data.items || []).forEach((it) => {
            if (it.resultado === 'LISTA') auto[it.case_id] = true;
          });
          setSelected(auto);
        }
      }
    } catch (e) {
      if (e.response?.status === 404) { setNotFound(true); setLoading(false); stopPoll(); }
    }
  }, [jobId]);

  useEffect(() => {
    autoSelectedRef.current = false;
    setLoading(true);
    tick();
    pollRef.current = setInterval(tick, 3000);
    return () => stopPoll();
  }, [tick]);

  const cancelarPrep = async () => {
    try {
      await api.post(`/cases/vigilar/preparar/${jobId}/cancelar`, {}, { skipAuthRedirect: true });
      toast.info('Cancelando chequeos…');
    } catch { /* noop */ }
  };

  const toggle = (cid) => setSelected((p) => ({ ...p, [cid]: !p[cid] }));

  const items = job?.items || [];
  const listas = items.filter((it) => it.resultado === 'LISTA');
  const selectedListaIds = listas.filter((it) => selected[it.case_id]).map((it) => it.case_id);
  const allListaSelected = listas.length > 0 && selectedListaIds.length === listas.length;
  const isProcessing = job && !TERMINAL.includes(job.estado);
  const resumen = job?.resumen || {};

  const toggleAllListas = () => {
    if (allListaSelected) {
      setSelected((p) => {
        const n = { ...p };
        listas.forEach((it) => { delete n[it.case_id]; });
        return n;
      });
    } else {
      setSelected((p) => {
        const n = { ...p };
        listas.forEach((it) => { n[it.case_id] = true; });
        return n;
      });
    }
  };

  const ejecutar = async () => {
    if (selectedListaIds.length === 0) { toast.error('Seleccioná al menos una cuenta "Lista"'); return; }
    if (!window.confirm(`Vas a correr la automatización (Playwright) para ${selectedListaIds.length} cuenta(s). ¿Continuar?`)) return;
    setEjecutando(true);
    try {
      const res = await api.post('/cases/vigilar', {
        case_ids: selectedListaIds,
        empresa_id: job.empresa_id,
        rubro_id: job.rubro_id,
      }, { skipAuthRedirect: true });
      toast.success(`Vigilancia iniciada para ${selectedListaIds.length} cuenta(s) en segundo plano.`);
      if (res.data?.job_id) {
        navigate('/m/gestion-de-casos/casos');
      }
    } catch (e) {
      toast.error(e.response?.data?.detail || 'No se pudo iniciar la vigilancia');
      setEjecutando(false);
    }
  };

  const progressPct = job?.total ? Math.round(((job.current_index || 0) / job.total) * 100) : 0;

  if (notFound) {
    return (
      <div className="p-6 max-w-3xl mx-auto" data-testid="preparar-vigilancia-notfound">
        <Button variant="outline" onClick={() => navigate('/m/gestion-de-casos/casos')} data-testid="prep-volver-btn">
          <ArrowLeft size={16} className="mr-2" /> Volver a Casos
        </Button>
        <Card className="p-8 mt-4 text-center">
          <AlertCircle className="mx-auto text-amber-500 mb-3" size={40} />
          <p className="text-slate-700 font-medium">No se encontró el chequeo de vigilancia.</p>
          <p className="text-slate-500 text-sm mt-1">Es posible que haya expirado. Volvé a iniciar el proceso desde Casos.</p>
        </Card>
      </div>
    );
  }

  return (
    <div className="p-6 max-w-5xl mx-auto" data-testid="preparar-vigilancia-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 mb-4">
        <div>
          <Button variant="ghost" size="sm" className="mb-1 -ml-2 text-slate-500"
            onClick={() => navigate('/m/gestion-de-casos/casos')} data-testid="prep-volver-btn">
            <ArrowLeft size={15} className="mr-1" /> Casos
          </Button>
          <h1 className="text-2xl font-bold text-slate-900 flex items-center gap-2">
            <Eye size={22} className="text-cyan-600" /> Preparar Vigilancia
          </h1>
          <p className="text-slate-500 text-sm mt-0.5">
            Fase 1: chequeo pasivo en Waya (email + estado de cuenta). Solo enviás a la automatización las cuentas aptas.
          </p>
        </div>
        <div className="flex items-center gap-2">
          {isProcessing && (
            <Button variant="outline" onClick={cancelarPrep} data-testid="prep-cancelar-btn"
              className="border-red-200 text-red-600 hover:bg-red-50">
              Cancelar chequeo
            </Button>
          )}
          <Button onClick={() => tick()} variant="outline" size="icon" title="Refrescar" data-testid="prep-refresh-btn">
            <RefreshCw size={16} />
          </Button>
        </div>
      </div>

      {loading && !job ? (
        <Card className="p-10 text-center">
          <Loader2 className="mx-auto animate-spin text-cyan-600 mb-2" size={28} />
          <p className="text-slate-500 text-sm">Cargando chequeo…</p>
        </Card>
      ) : (
        <>
          {/* Progreso + resumen */}
          <Card className="p-4 mb-4">
            <div className="flex items-center justify-between mb-2">
              <div className="text-sm font-medium text-slate-700 flex items-center gap-2" data-testid="prep-current-step">
                {isProcessing && <Loader2 size={15} className="animate-spin text-blue-600" />}
                {job?.current_step || 'Chequeos finalizados'}
              </div>
              <Badge className={isProcessing ? 'bg-blue-100 text-blue-700' : job?.estado === 'CANCELADO' ? 'bg-slate-200 text-slate-600' : 'bg-green-100 text-green-700'}>
                {job?.estado}
              </Badge>
            </div>
            <div className="w-full h-2 bg-slate-100 rounded-full overflow-hidden">
              <div className="h-full bg-cyan-500 transition-all" style={{ width: `${isProcessing ? progressPct : 100}%` }} />
            </div>
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mt-4">
              <SemCard color="green" label="Listas" value={resumen.listas} Icon={CheckCircle2} testid="prep-resumen-listas" />
              <SemCard color="amber" label="Ya configuradas" value={resumen.ya_config} Icon={ShieldCheck} testid="prep-resumen-yaconfig" />
              <SemCard color="red" label="Bloqueadas" value={resumen.bloqueadas} Icon={Ban} testid="prep-resumen-bloqueadas" />
              <SemCard color="gray" label="Sin email" value={resumen.sin_mail} Icon={MailX} testid="prep-resumen-sinmail" />
            </div>
          </Card>

          {/* Tabla de items */}
          <Card className="overflow-hidden">
            <div className="flex items-center justify-between px-4 py-3 border-b border-slate-100 bg-slate-50">
              <label className="flex items-center gap-2 text-sm text-slate-600 cursor-pointer">
                <Checkbox checked={allListaSelected} onCheckedChange={toggleAllListas}
                  disabled={listas.length === 0} data-testid="prep-select-all" />
                Seleccionar todas las listas ({listas.length})
              </label>
              <span className="text-xs text-slate-500">{items.length} caso(s) chequeado(s)</span>
            </div>
            <div className="divide-y divide-slate-100">
              {items.map((it) => {
                const meta = RESULT_META[it.resultado] || RESULT_META.PENDIENTE;
                const { Icon } = meta;
                const isLista = it.resultado === 'LISTA';
                const changed = rowChanged[it.case_id];
                const busy = rowBusy[it.case_id];
                return (
                  <div key={it.case_id} className="flex items-center gap-3 px-4 py-3 hover:bg-slate-50"
                    data-testid={`prep-item-${it.case_id}`}>
                    <Checkbox checked={!!selected[it.case_id]} onCheckedChange={() => toggle(it.case_id)}
                      disabled={!isLista || !!changed} data-testid={`prep-item-check-${it.case_id}`} />
                    <div className="flex-1 min-w-0">
                      <div className="font-medium text-slate-800 text-sm truncate">{it.titulo}</div>
                      <div className="text-xs text-slate-500 truncate">
                        {it.email ? <span className="text-slate-600">{it.email}</span> : <span className="italic">sin email</span>}
                        {it.estado_cuenta ? <span className="ml-2 text-slate-400">· {it.estado_cuenta}</span> : null}
                        {it.mensaje ? <span className="ml-2 text-slate-400">· {it.mensaje}</span> : null}
                      </div>
                    </div>
                    <Badge variant="outline" className={`${meta.cls} flex items-center gap-1 shrink-0`}
                      data-testid={`prep-badge-${it.case_id}`}>
                      <Icon size={12} className={meta.spin ? 'animate-spin' : ''} />
                      {meta.label}
                    </Badge>
                    {/* Acción por caso: cambiar estado / cerrar */}
                    {changed ? (
                      <Badge className="bg-emerald-100 text-emerald-700 border-emerald-200 shrink-0 flex items-center gap-1"
                        data-testid={`prep-changed-${it.case_id}`}>
                        <CheckCircle2 size={12} /> → {changed}
                      </Badge>
                    ) : (
                      <div className="shrink-0 flex items-center gap-1">
                        {busy && <Loader2 size={13} className="animate-spin text-slate-400" />}
                        <select
                          value=""
                          onChange={(e) => { cambiarEstado(it.case_id, e.target.value); e.target.value = ''; }}
                          disabled={busy}
                          className="text-xs border border-slate-200 rounded-md px-1.5 py-1 bg-white text-slate-600 focus:outline-none focus:ring-1 focus:ring-cyan-400 max-w-[140px]"
                          data-testid={`prep-action-${it.case_id}`}
                          title="Cambiar estado o cerrar este caso"
                        >
                          <option value="">Acción…</option>
                          <option value="__CERRAR__">🔒 Cerrar caso</option>
                          <optgroup label="Cambiar estado a:">
                            {allStates.map((s) => (
                              <option key={s.value} value={s.value}>{s.label}</option>
                            ))}
                          </optgroup>
                        </select>
                      </div>
                    )}
                  </div>
                );
              })}
              {items.length === 0 && (
                <div className="px-4 py-8 text-center text-slate-400 text-sm">Sin casos para chequear.</div>
              )}
            </div>
          </Card>

          {/* Footer acción */}
          <div className="sticky bottom-0 mt-4 bg-white/90 backdrop-blur border-t border-slate-200 py-3 flex items-center justify-between rounded-b-lg px-1">
            <div className="text-sm text-slate-600">
              <span className="font-semibold text-cyan-700">{selectedListaIds.length}</span> cuenta(s) seleccionada(s) para vigilar
              {isProcessing && <span className="text-amber-600 ml-2">(esperá a que termine el chequeo)</span>}
            </div>
            <Button onClick={ejecutar} disabled={isProcessing || ejecutando || selectedListaIds.length === 0}
              className="bg-cyan-600 hover:bg-cyan-700 text-white" data-testid="prep-ejecutar-btn">
              {ejecutando ? <Loader2 size={16} className="mr-2 animate-spin" /> : <Eye size={16} className="mr-2" />}
              Ejecutar Vigilancia ({selectedListaIds.length})
            </Button>
          </div>
        </>
      )}

      {/* Mini diálogo de cierre de caso */}
      <Dialog open={!!closeDlg} onOpenChange={(o) => { if (!o) setCloseDlg(null); }}>
        <DialogContent className="max-w-md" data-testid="prep-close-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <XCircle size={18} className="text-red-600" /> Cerrar caso
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-3">
            <p className="text-xs text-slate-500 truncate">{closeDlg?.titulo}</p>
            <div>
              <label className="text-sm font-medium text-slate-700 block mb-1">Tipo de resolución</label>
              <select
                value={closeTipo}
                onChange={(e) => setCloseTipo(e.target.value)}
                className="w-full text-sm border border-slate-200 rounded-lg px-3 py-2 bg-white focus:outline-none focus:ring-2 focus:ring-red-300"
                data-testid="prep-close-tipo"
              >
                {tiposResolucion.length === 0 && <option value="Otro">Otro</option>}
                {tiposResolucion.map((t) => <option key={t} value={t}>{t}</option>)}
              </select>
            </div>
            <div>
              <label className="text-sm font-medium text-slate-700 block mb-1">Observación</label>
              <Textarea
                value={closeObs}
                onChange={(e) => setCloseObs(e.target.value)}
                placeholder="Ej: Cuenta bloqueada en Waya"
                rows={3}
                data-testid="prep-close-obs"
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setCloseDlg(null)}>Cancelar</Button>
            <Button onClick={doClose} disabled={closing} className="bg-red-600 hover:bg-red-700 text-white"
              data-testid="prep-close-confirm">
              {closing ? <Loader2 size={16} className="mr-2 animate-spin" /> : <XCircle size={16} className="mr-2" />}
              Cerrar caso
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

const COLOR_CLS = {
  green: 'bg-green-50 border-green-200 text-green-700',
  amber: 'bg-amber-50 border-amber-200 text-amber-700',
  red: 'bg-red-50 border-red-200 text-red-700',
  gray: 'bg-gray-50 border-gray-200 text-gray-600',
};

function SemCard({ color, label, value, Icon, testid }) {
  return (
    <div className={`rounded-lg border p-3 ${COLOR_CLS[color]}`} data-testid={testid}>
      <div className="flex items-center gap-1.5 text-xs font-medium opacity-80">
        <Icon size={13} /> {label}
      </div>
      <div className="text-2xl font-bold mt-1">{value || 0}</div>
    </div>
  );
}
